import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalendarAlt, FaMapMarkerAlt, FaClock, FaFilter, FaSearch, FaChevronDown, FaPlus, FaStar, FaBullseye, FaFutbol, FaTrophy, FaUsers, FaChartLine, FaHome, FaRoute } from 'react-icons/fa';
import { fetchEquipesChampionnat, fetchAllClubMatches, CLUB_ID } from '../api/fff';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import useInView from '../hooks/useInView';
import APIDebugPanel from '../components/Layout/APIDebugPanel';

dayjs.locale('fr');

const Matchs = () => {
  const [matchs, setMatchs] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEquipe, setSelectedEquipe] = useState('toutes');
  const [selectedPeriode, setSelectedPeriode] = useState('prochains');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [selectedLieu, setSelectedLieu] = useState('');

  // Refs pour animations
  const [headerRef, headerInView] = useInView({ threshold: 0.2 });
  const [statsRef, statsInView] = useInView({ threshold: 0.2 });
  const [filtersRef, filtersInView] = useInView({ threshold: 0.2 });
  const [matchsRef, matchsInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // 1. Charger toutes les équipes et tous les matchs en parallèle
      const [equipesData, allMatchesRaw] = await Promise.all([
        fetchEquipesChampionnat(),
        fetchAllClubMatches()
      ]);

      setEquipes(equipesData);

      // 2. Associer chaque match à son équipe locale
      let matchsAssocies = [];
      let matchsNonAssocies = 0;

      allMatchesRaw.forEach(match => {
        // Identifier l'équipe du club concernée par ce match
        // On cherche une équipe dont le numéro correspond au numéro de l'équipe domicile ou extérieur (si c'est le club)
        let monEquipe = null;

        if (match.home.club.cl_no === CLUB_ID) {
          monEquipe = equipesData.find(e => e.number === match.home.number);
        } else if (match.away.club.cl_no === CLUB_ID) {
          monEquipe = equipesData.find(e => e.number === match.away.number);
        }

        if (monEquipe) {
          matchsAssocies.push({
            ...match,
            equipeNom: monEquipe.nom,
            equipeId: monEquipe.fffId, // fffId est le numéro d'équipe
            championnat: monEquipe.championnat
          });
        } else {
          matchsNonAssocies++;
          // Optionnel : log pour debug
          // console.warn("Match non associé à une équipe connue:", match);
        }
      });

      console.log(`📊 Résumé chargement:`, {
        équipes: equipesData.length,
        matchsTotalRecuperes: allMatchesRaw.length,
        matchsAssocies: matchsAssocies.length,
        matchsNonAssocies: matchsNonAssocies
      });

      // 3. Trier par date
      matchsAssocies.sort((a, b) => new Date(a.date) - new Date(b.date));
      setMatchs(matchsAssocies);

    } catch (error) {
      console.error('Erreur loadData:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les matchs
  const matchsFiltres = matchs.filter(match => {
    // Filtre par équipe
    if (selectedEquipe !== 'toutes' && match.equipeId !== selectedEquipe) {
      return false;
    }

    // Filtre par période
    const now = new Date();
    const matchDate = new Date(match.date);

    if (selectedPeriode === 'prochains' && matchDate <= now) return false;

    if (selectedPeriode === 'passes') {
      // ✅ CORRECTION: Vérifier que le match est passé ET qu'il a un score
      const hasScore = match.home_score !== null && match.home_score !== undefined &&
        match.away_score !== null && match.away_score !== undefined;
      if (matchDate > now || !hasScore) return false;
    }

    if (selectedPeriode === '7jours') {
      const dans7Jours = new Date();
      dans7Jours.setDate(dans7Jours.getDate() + 7);
      if (matchDate <= now || matchDate > dans7Jours) return false;
    }

    // Filtre par compétition
    if (selectedCompetition && match.championnat !== selectedCompetition) {
      return false;
    }

    // Filtre par lieu
    // ✅ CORRECTION: Vérifier FEIGNIES ou AULNOYE au lieu de EFAFC
    const homeTeamName = match.home?.short_name_federation?.toLowerCase() || '';
    const isEfafcHome = homeTeamName.includes('feignies') ||
      homeTeamName.includes('aulnoye') ||
      homeTeamName.includes('efafc');

    if (selectedLieu === 'domicile' && !isEfafcHome) return false;
    if (selectedLieu === 'exterieur' && isEfafcHome) return false;

    // Filtre par recherche
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return match.home.short_name_federation?.toLowerCase().includes(search) ||
        match.away.short_name_federation?.toLowerCase().includes(search) ||
        match.equipeNom?.toLowerCase().includes(search) ||
        match.terrain?.name?.toLowerCase().includes(search);
    }

    return true;
  });

  const addToCalendar = (match) => {
    const startDate = new Date(`${match.date}T${match.time || '15:00'}`);
    const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000); // +2h

    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const title = `${match.home.short_name_federation} vs ${match.away.short_name_federation}`;
    const location = match.terrain ? `${match.terrain.name}, ${match.terrain.city}` : '';
    const details = `Match de ${match.equipeNom} - ${match.competition?.name || ''}`;

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${formatDate(startDate)}/${formatDate(endDate)}&location=${encodeURIComponent(location)}&details=${encodeURIComponent(details)}`;

    window.open(googleCalendarUrl, '_blank');
  };

  const getMatchStatus = (match) => {
    const now = new Date();
    const matchDate = new Date(match.date);

    if (match.home_score !== null && match.away_score !== null) {
      return { status: 'termine', label: 'Terminé', color: 'from-green-500 to-green-600', icon: FaTrophy };
    } else if (matchDate < now) {
      return { status: 'reporte', label: 'Reporté', color: 'from-orange-500 to-orange-600', icon: FaClock };
    } else {
      const diffHours = (matchDate - now) / (1000 * 60 * 60);
      if (diffHours <= 24) {
        return { status: 'imminent', label: 'Imminent', color: 'from-red-500 to-red-600', icon: FaFutbol };
      } else if (diffHours <= 168) { // 7 jours
        return { status: 'proche', label: 'Cette semaine', color: 'from-blue-500 to-blue-600', icon: FaCalendarAlt };
      } else {
        return { status: 'programme', label: 'Programmé', color: 'from-gray-500 to-gray-600', icon: FaCalendarAlt };
      }
    }
  };

  const getCompetitions = () => {
    return [...new Set(matchs.map(m => m.championnat))].filter(Boolean);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002766] to-[#001B56] pt-20 md:pt-24">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <div className="w-16 h-16 border-4 border-[#FFE400] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="text-2xl font-title font-bold text-white mb-2">Chargement des matchs...</h2>
            <p className="text-white/70 font-accent">Récupération du calendrier EFAFC</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002766] to-[#001B56] pt-20 md:pt-24">
      {/* Effets de fond */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.3, 0.1]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FFE400] rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#BDFF00] rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 max-w-[1600px]">
        {/* Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 30 }}
          animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-[#001B56] to-[#002766] rounded-3xl blur opacity-20"></div>
            <div className="relative bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-white/10">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={headerInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="inline-block p-4 bg-gradient-to-br from-[#FFE400] to-[#BDFF00] rounded-2xl mb-6"
              >
                <FaCalendarAlt className="text-3xl text-[#001B56]" />
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-title font-extrabold text-white mb-4">
                CALENDRIER
                <motion.span
                  className="block text-[#FFE400] text-3xl md:text-4xl mt-2"
                  initial={{ opacity: 0, x: -50 }}
                  animate={headerInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                  transition={{ delay: 0.6 }}
                >
                  DES MATCHS
                </motion.span>
              </h1>

              <p className="text-white/80 text-lg font-accent max-w-2xl mx-auto">
                Suivez tous les matchs de nos équipes et ne manquez aucune rencontre
              </p>
            </div>
          </div>
        </motion.div>

        {/* Statistiques rapides */}
        <motion.div
          ref={statsRef}
          initial={{ opacity: 0, y: 30 }}
          animate={statsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12"
        >
          {[
            {
              icon: FaCalendarAlt,
              label: 'Prochains matchs',
              value: matchs.filter(m => new Date(m.date) > new Date()).length,
              color: '#FFE400'
            },
            {
              icon: FaUsers,
              label: 'Équipes',
              value: equipes.length,
              color: '#BDFF00'
            },
            {
              icon: FaTrophy,
              label: 'Matchs joués',
              value: matchs.filter(m => m.home_score !== null).length,
              color: '#FFE400'
            },
            {
              icon: FaChartLine,
              label: 'Compétitions',
              value: new Set(matchs.map(m => m.championnat)).size,
              color: '#BDFF00'
            }
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={statsInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
              transition={{ delay: 0.1 * index }}
              className="bg-white/5 backdrop-blur-md rounded-xl p-6 border border-white/10 text-center hover:bg-white/10 hover:border-white/15 transition-all duration-300"
            >
              <stat.icon className="text-3xl mb-3 mx-auto" style={{ color: stat.color }} />
              <div className="text-2xl font-title font-bold text-white mb-1">{stat.value}</div>
              <div className="text-white/60 text-sm font-accent">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Filtres */}
        <motion.div
          ref={filtersRef}
          initial={{ opacity: 0, y: 30 }}
          animate={filtersInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-8"
        >
          <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
            <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
              {/* Recherche */}
              <div className="relative flex-1 max-w-md">
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60" />
                <input
                  type="text"
                  placeholder="Rechercher un match, équipe, stade..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-2xl font-accent text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-[#FFE400] focus:border-transparent backdrop-blur-md transition-all duration-300"
                />
              </div>

              {/* Filtres rapides */}
              <div className="flex flex-wrap gap-3">
                <select
                  value={selectedPeriode}
                  onChange={(e) => setSelectedPeriode(e.target.value)}
                  className="px-4 py-3 bg-white/10 border border-white/20 rounded-2xl font-accent text-white focus:outline-none focus:ring-2 focus:ring-[#FFE400] backdrop-blur-md transition-all duration-300"
                >
                  <option value="tous" className="bg-[#001B56] text-white">Tous les matchs</option>
                  <option value="prochains" className="bg-[#001B56] text-white">Prochains matchs</option>
                  <option value="7jours" className="bg-[#001B56] text-white">Cette semaine</option>
                  <option value="passes" className="bg-[#001B56] text-white">Matchs passés</option>
                </select>

                <select
                  value={selectedEquipe}
                  onChange={(e) => setSelectedEquipe(e.target.value)}
                  className="px-4 py-3 bg-white/10 border border-white/20 rounded-2xl font-accent text-white focus:outline-none focus:ring-2 focus:ring-[#FFE400] backdrop-blur-md transition-all duration-300"
                >
                  <option value="toutes" className="bg-[#001B56] text-white">Toutes les équipes</option>
                  {equipes.map(equipe => (
                    <option key={equipe.fffId} value={equipe.fffId} className="bg-[#001B56] text-white">
                      {equipe.nom}
                    </option>
                  ))}
                </select>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-2xl font-accent font-bold hover:shadow-lg transition-all duration-300"
                >
                  <FaFilter />
                  Filtres avancés
                  <FaChevronDown className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </motion.button>
              </div>
            </div>

            {/* Filtres avancés */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-6 pt-6 border-t border-white/20"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block font-accent text-sm text-white/80 mb-2">Compétition</label>
                      <select
                        value={selectedCompetition}
                        onChange={(e) => setSelectedCompetition(e.target.value)}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl font-accent text-white focus:outline-none focus:ring-2 focus:ring-[#FFE400] backdrop-blur-md transition-all duration-300"
                      >
                        <option value="" className="bg-[#001B56] text-white">Toutes les compétitions</option>
                        {getCompetitions().map(comp => (
                          <option key={comp} value={comp} className="bg-[#001B56] text-white">{comp}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block font-accent text-sm text-white/80 mb-2">Lieu</label>
                      <select
                        value={selectedLieu}
                        onChange={(e) => setSelectedLieu(e.target.value)}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl font-accent text-white focus:outline-none focus:ring-2 focus:ring-[#FFE400] backdrop-blur-md transition-all duration-300"
                      >
                        <option value="" className="bg-[#001B56] text-white">Tous les lieux</option>
                        <option value="domicile" className="bg-[#001B56] text-white">À domicile</option>
                        <option value="exterieur" className="bg-[#001B56] text-white">À l'extérieur</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-accent text-sm text-white/80 mb-2">Période</label>
                      <input
                        type="month"
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl font-accent text-white focus:outline-none focus:ring-2 focus:ring-[#FFE400] backdrop-blur-md transition-all duration-300"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Résultats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mb-6"
        >
          <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 border border-white/10 inline-block">
            <p className="font-accent text-white/80">
              <span className="text-[#FFE400] font-bold">{matchsFiltres.length}</span> match{matchsFiltres.length > 1 ? 's' : ''} trouvé{matchsFiltres.length > 1 ? 's' : ''}
            </p>
          </div>
        </motion.div>

        {/* Liste des matchs */}
        <motion.div
          ref={matchsRef}
          initial={{ opacity: 0, y: 30 }}
          animate={matchsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <AnimatePresence>
            {matchsFiltres.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16"
              >
                <div className="bg-white/5 backdrop-blur-md rounded-2xl p-12 border border-white/10 max-w-md mx-auto">
                  <div className="text-6xl mb-6">⚽</div>
                  <h3 className="text-2xl font-title font-bold text-white mb-4">Aucun match trouvé</h3>
                  <p className="text-white/70 font-accent">
                    Modifiez vos critères de recherche pour voir plus de résultats
                  </p>
                </div>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {matchsFiltres.map((match, index) => {
                  const status = getMatchStatus(match);
                  const StatusIcon = status.icon;

                  return (
                    <motion.div
                      key={`${match.equipeId}-${match.date}-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5, scale: 1.02 }}
                      className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-white/15 transition-all duration-300 group"
                    >
                      {/* Header du match */}
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <span className="bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] px-4 py-2 rounded-full text-sm font-accent font-bold">
                            {match.equipeNom}
                          </span>
                          <span className="bg-white/10 text-white/80 px-3 py-1 rounded-full text-sm font-accent border border-white/20">
                            {match.championnat}
                          </span>
                        </div>
                        <div className={`bg-gradient-to-r ${status.color} text-white px-4 py-2 rounded-full text-sm font-accent font-bold flex items-center gap-2 shadow-lg`}>
                          <StatusIcon className="text-sm" />
                          {status.label}
                        </div>
                      </div>

                      {/* Équipes et score */}
                      <div className="flex items-center justify-between mb-8">
                        <div className="text-center flex-1">
                          <div className="flex items-center justify-center gap-2 mb-3">
                            <FaHome className="text-[#FFE400]" />
                            <div className="font-title text-lg font-bold text-white">
                              {match.home.short_name_federation}
                            </div>
                          </div>
                          <div className="text-sm text-white/60 font-accent">Domicile</div>
                        </div>

                        <div className="mx-8 text-center">
                          {match.home_score !== null && match.away_score !== null ? (
                            <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 border border-white/10">
                              <div className="font-title text-3xl font-bold text-[#FFE400] mb-1">
                                {match.home_score} - {match.away_score}
                              </div>
                              <div className="text-xs text-white/60 font-accent">Score final</div>
                            </div>
                          ) : (
                            <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 border border-white/10">
                              <div className="font-title text-2xl font-bold text-white/60 mb-1">
                                VS
                              </div>
                              <div className="text-xs text-white/60 font-accent">À venir</div>
                            </div>
                          )}
                        </div>

                        <div className="text-center flex-1">
                          <div className="flex items-center justify-center gap-2 mb-3">
                            <FaRoute className="text-[#BDFF00]" />
                            <div className="font-title text-lg font-bold text-white">
                              {match.away.short_name_federation}
                            </div>
                          </div>
                          <div className="text-sm text-white/60 font-accent">Extérieur</div>
                        </div>
                      </div>

                      {/* Informations du match */}
                      <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="flex items-center gap-3 text-white/80">
                            <FaCalendarAlt className="text-[#FFE400]" />
                            <div>
                              <div className="font-accent font-bold text-sm">
                                {dayjs(match.date).format('dddd D MMMM')}
                              </div>
                              <div className="text-xs text-white/60">
                                {dayjs(match.date).format('YYYY')}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-white/80">
                            <FaClock className="text-[#BDFF00]" />
                            <div>
                              <div className="font-accent font-bold text-sm">{match.time || '15:00'}</div>
                              <div className="text-xs text-white/60">Heure de début</div>
                            </div>
                          </div>
                          {match.terrain && (
                            <div className="flex items-center gap-3 text-white/80">
                              <FaMapMarkerAlt className="text-[#FFE400]" />
                              <div>
                                <div className="font-accent font-bold text-sm">{match.terrain.name}</div>
                                <div className="text-xs text-white/60">{match.terrain.city}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-3">
                        {status.status !== 'termine' && (
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => addToCalendar(match)}
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#BDFF00] to-[#FFE400] text-[#001B56] rounded-xl font-accent font-bold hover:shadow-lg transition-all duration-300"
                          >
                            <FaPlus />
                            Calendrier
                          </motion.button>
                        )}

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="flex items-center gap-2 px-4 py-2 bg-white/5 text-white rounded-xl font-accent font-bold hover:bg-white/10 border border-white/10 backdrop-blur-md transition-all duration-300"
                        >
                          <FaStar />
                          Suivre
                        </motion.button>

                        {status.status !== 'termine' && (
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-xl font-accent font-bold hover:shadow-lg transition-all duration-300"
                          >
                            <FaBullseye />
                            Pronostiquer
                          </motion.button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Panel de diagnostic API (développement uniquement) */}
      <APIDebugPanel />
    </div>
  );
};

export default Matchs;